import React, { useCallback, useContext } from 'react';

import { useQueryClient } from '@lumapps/base-api/react-query';
import { cache, CACHE_TYPE } from '@lumapps/cache';
import { useClassnames } from '@lumapps/classnames';
import { getPermissionsForCustomContentType } from '@lumapps/content-types/ducks/selectors';
import { CUSTOM_CONTENT_TYPE_PERMISSIONS } from '@lumapps/content-types/permissions';
import { contentQueryKeys } from '@lumapps/contents/api/contents';
import { ContentStatusFlag } from '@lumapps/contents/components/ContentStatusFlag';
import { useContentArchive } from '@lumapps/contents/hooks/useContentArchive';
import { useContentUnpublish } from '@lumapps/contents/hooks/useContentUnpublish';
import { usePublicationConfirmDialog } from '@lumapps/contents/hooks/usePublicationConfirmDialog';
import { useValidationWorkflowActions } from '@lumapps/contents/hooks/useValidationWorkflowActions';
import { CONTENTS } from '@lumapps/contents/keys';
import type { Content, WriteModeContent } from '@lumapps/contents/types';
import { useDataAttributes } from '@lumapps/data-attributes';
import { ContentLanguagePopover } from '@lumapps/designer/components/ContentLanguagePopover';
import { ContentScheduleDialog } from '@lumapps/designer/components/ContentScheduleDialog';
import { ContentSummaryDialog } from '@lumapps/designer/components/ContentSummaryDialog';
import { TOPBAR_CLASSNAME, CONTENT_EDITOR_TOPBAR_SCOPE } from '@lumapps/designer/constants';
import { useContentScheduling } from '@lumapps/designer/hooks/useContentScheduling';
import { useContentValidity } from '@lumapps/designer/hooks/useContentValidity';
import { usePublicationSummaryDialog } from '@lumapps/designer/hooks/usePublicationSummaryDialog';
import { DESIGNER } from '@lumapps/designer/keys';
import { ConfirmDialog } from '@lumapps/lumx-dialogs/components/ConfirmDialog';
import {
    mdiArchiveArrowUpOutline,
    mdiArrowRight,
    mdiArrowUp,
    mdiAutorenew,
    mdiContentSave,
    mdiTune,
} from '@lumapps/lumx/icons';
import {
    Button,
    ButtonGroup,
    FlexBox,
    Heading,
    IconButton,
    SkeletonRectangle,
    SkeletonTypography,
} from '@lumapps/lumx/react';
import { useSelector } from '@lumapps/redux/react';
import { useResponsive } from '@lumapps/responsive';
import { GLOBAL, toCompatibleLanguage, useTranslate } from '@lumapps/translations';
import { LanguageSelectButton } from '@lumapps/translations/components/LanguageSelectButton/LanguageSelectButton';
import { contributionLanguagesSelector, inputLanguageSelector } from '@lumapps/translations/ducks/selectors';

import { LOCAL_STORAGE_CONTENT_LANGUAGE_POPOVER } from '../../constants';
import { ContentEditorContext } from '../../context/ContentEditorContext';
import { CloseButton } from './CloseButton';
import { ContentStatusMetadata } from './ContentStatusMetadata';
import { SecondaryActionsMenu } from './SecondaryActionsMenu';

import './index.scss';

export interface ContentEditorTopbarProps {
    /** Check if content has changes in the parent legacy editor */
    hasChanges?: () => boolean;
    /** Whether the versionning panel is open or not. Information from legacy. */
    isVersionningPanelOpen?: boolean;
    isLoading?: boolean;
    isLegacy?: boolean;
    currentContent: WriteModeContent;
    settingsTriggerRef?: React.RefObject<HTMLButtonElement>;
}

/**
 *
 * @param ContentEditorTopbarProps
 * @returns ContentEditorTopbar
 */
export const ContentEditorTopbar: React.FC<ContentEditorTopbarProps> = ({
    currentContent,
    hasChanges,
    isVersionningPanelOpen,
    settingsTriggerRef,
    isLegacy,
    isLoading,
}) => {
    const isLanguagePopoverSkipped = Boolean(
        cache.retrieve(LOCAL_STORAGE_CONTENT_LANGUAGE_POPOVER, CACHE_TYPE.STORAGE),
    );

    const { publicationInformations, contentEditorActions } = useContext(ContentEditorContext);
    const { publishContent } = contentEditorActions;

    const contentId = currentContent.id;

    const queryClient = useQueryClient();

    const { get } = useDataAttributes(CONTENT_EDITOR_TOPBAR_SCOPE);
    const { element } = useClassnames(TOPBAR_CLASSNAME);
    const { translateKey, translateObject } = useTranslate();
    const { isDesktop: isUnderDesktop } = useResponsive();

    const isContentValid = useContentValidity();

    const {
        expiredAt,
        publishedAt,
        scheduledAt,
        hasPendingDraft,
        isAwaitingValidation,
        isRevisionRequested,
        publicationStatus,
    } = publicationInformations!;

    const titleObject = currentContent.title;

    const scheduledDate = currentContent.startDate;

    const { isHomepage } = currentContent.liveRevision || {};

    const { customContentTypeDetails, customContentType } = currentContent;
    const updatedAt = currentContent.updatedAt || currentContent.lastRevision?.updatedAt;
    const { createdByDetails } = currentContent.lastRevision || {};
    const contentProperties = currentContent.properties;

    const isNewDraft = publicationStatus === 'draft' && !updatedAt;
    const refusedReason = contentProperties?.publicationError;

    const isWorkflowEnabled = customContentTypeDetails?.isWorkflowEnabled;
    const isExpirationDateMandatory = customContentTypeDetails?.isEndDateMandatory;
    const contributionLanguages = useSelector(contributionLanguagesSelector);
    // Use apiv1 regional language instead of v2
    // e.g use pt_br instead of pt-BR on language selection
    const cleanedContributionLanguages = contributionLanguages.map((lang) => toCompatibleLanguage(lang));
    const inputLanguage = toCompatibleLanguage(useSelector(inputLanguageSelector));
    const [isLanguagePopoverOpen, setIsLanguagePopoverOpen] = React.useState(true);

    const languageSelectRef = React.useRef(null);
    const title = titleObject?.[inputLanguage];
    const contentTypeAuthorizations = useSelector(getPermissionsForCustomContentType(customContentType || ''));

    const canSave = !isAwaitingValidation && !isVersionningPanelOpen && publicationStatus !== 'archived';

    const canArchive =
        contentTypeAuthorizations.includes(CUSTOM_CONTENT_TYPE_PERMISSIONS.CUSTOM_CONTENT_ARCHIVE) && !isNewDraft;
    const canUnarchive = publicationStatus === 'archived' && canArchive;

    const canPublish = isWorkflowEnabled
        ? contentTypeAuthorizations.includes(CUSTOM_CONTENT_TYPE_PERMISSIONS.CUSTOM_CONTENT_PUBLISH)
        : contentTypeAuthorizations.includes(CUSTOM_CONTENT_TYPE_PERMISSIONS.CUSTOM_CONTENT_EDIT);

    const { closeConfirmDialog, dialogProps: confirmDialogProps, openConfirmDialog } = usePublicationConfirmDialog();
    const handleUpdateSuccess = React.useCallback(
        (content: Content) => {
            contentEditorActions.updateContent({ content, isPartial: false });
            queryClient.invalidateQueries({ queryKey: contentQueryKeys.getLiveRevision(content.id!) });
        },
        [contentEditorActions, queryClient],
    );

    const { onArchive, onUnarchive } = useContentArchive({
        openConfirmDialog,
        contentId,
        onSuccess: handleUpdateSuccess,
        contentTitle: translateObject(titleObject) || '',
    });

    const { onUnpublish } = useContentUnpublish({
        openConfirmDialog,
        contentId,
        onSuccess: handleUpdateSuccess,
        contentTitle: translateObject(titleObject) || '',
    });

    const { openSummaryDialog, dialogProps: publicationSummaryDialogProps } = usePublicationSummaryDialog({
        onSettingsOpen: contentEditorActions.openContentSettings,
    });

    const { onAccept, onRequestRevision, onSubmitForReview } = useValidationWorkflowActions({
        openConfirmDialog,
        contentId,
        saveContent: contentEditorActions.saveContent,
        onSuccess: (content) => contentEditorActions.updateContent({ content, isPartial: false }),
    });

    const publishContentNow = useCallback(() => {
        if (!isExpirationDateMandatory && publicationStatus === 'expired') {
            // We need to reset end date when publishing an expired content
            contentEditorActions.updateContent({
                content: { endDate: undefined, endDateInput: undefined },
                isPartial: true,
            });
        }

        if (isContentValid) {
            openSummaryDialog({
                publicationSourceType: 'publish',
                onConfirm: async () => {
                    contentEditorActions.updateContent({
                        content: { startDate: undefined, startDateInput: undefined },
                        isPartial: true,
                    });
                    try {
                        const { id } = await publishContent();

                        queryClient.invalidateQueries({ queryKey: contentQueryKeys.getLiveRevision(id!) });
                    } catch (err) {
                        // Error notification is handled by the legacy publish function.
                    }
                },
            });
        } else {
            /**
             * @TODO When content settings will be migrated we will be able to
             * open the settings with errors. But for now we simply try to publish to trigger the
             * legacy content form submit.
             */
            publishContent();
        }
    }, [
        contentEditorActions,
        isContentValid,
        isExpirationDateMandatory,
        openSummaryDialog,
        publicationStatus,
        publishContent,
        queryClient,
    ]);

    const acceptWithReview = useCallback(() => {
        openSummaryDialog({
            onConfirm: onAccept,
            publicationSourceType: scheduledDate ? 'schedule' : 'publish',
        });
    }, [openSummaryDialog, onAccept, scheduledDate]);

    const quickAction = React.useMemo(() => {
        if (isWorkflowEnabled) {
            if (!isAwaitingValidation) {
                return {
                    label: translateKey(CONTENTS.SUBMIT_FOR_REVIEW_LABEL),
                    onClick: onSubmitForReview,
                    shortIcon: mdiArrowRight,
                    props: {
                        ...get({ element: 'publication-workflow', action: 'submit-for-review' }),
                    },
                };
            }

            if (publicationStatus === 'published') {
                return {
                    label: translateKey(DESIGNER.PUBLISH_CHANGES_LABEL),
                    onClick: acceptWithReview,
                    shortIcon: mdiAutorenew,
                    props: {
                        ...get({ element: 'publication-workflow', action: 'publish-changes' }),
                    },
                };
            }

            return {
                label: translateKey(GLOBAL.PUBLISH),
                onClick: acceptWithReview,
                shortIcon: mdiArrowUp,
                props: {
                    ...get({ element: 'publication-workflow', action: 'publish' }),
                },
            };
        }

        if (publicationStatus === 'scheduled' || publicationStatus === 'published') {
            return {
                label: translateKey(DESIGNER.PUBLISH_CHANGES_LABEL),
                onClick: async () => {
                    if (publicationStatus === 'published') {
                        contentEditorActions.updateContent({
                            content: { startDate: undefined, startDateInput: undefined },
                            isPartial: true,
                        });
                        try {
                            const { id } = await publishContent();
                            if (id) {
                                queryClient.invalidateQueries({ queryKey: contentQueryKeys.getLiveRevision(id) });
                            }
                        } catch (err) {
                            // Error notification is handled by the legacy publish function.
                        }
                    } else {
                        publishContent();
                    }
                },
                shortIcon: mdiAutorenew,
                props: {
                    ...get({ element: 'publication-workflow', action: 'publish-changes' }),
                },
            };
        }

        // If draft or expired.
        return {
            label: translateKey(GLOBAL.PUBLISH),
            onClick: publishContentNow,
            shortIcon: mdiArrowUp,
            props: {
                ...get({ element: 'publication-workflow', action: 'publish' }),
            },
        };
    }, [
        acceptWithReview,
        contentEditorActions,
        get,
        isAwaitingValidation,
        isWorkflowEnabled,
        onSubmitForReview,
        publicationStatus,
        publishContent,
        publishContentNow,
        queryClient,
        translateKey,
    ]);

    const {
        openScheduleDialog,
        dialogProps: scheduleDialogProps,
        unscheduleContent,
    } = useContentScheduling({
        contentId,
        updateContent: contentEditorActions.updateContent,
        saveContent: contentEditorActions.saveContent,
        openSummaryDialog,
        publishContent,
    });

    const onContributionLanguageChange = (lang: string) => {
        contentEditorActions.onInputLanguageChange(lang);
        setIsLanguagePopoverOpen(false);
    };

    return (
        <>
            <ConfirmDialog {...confirmDialogProps} onClose={closeConfirmDialog} />
            <ContentSummaryDialog
                {...publicationSummaryDialogProps}
                currentContent={currentContent}
                scope="content-publication-summary"
            />
            <ContentScheduleDialog {...scheduleDialogProps} />
            <FlexBox
                role="toolbar"
                aria-labelledby="toolbar-title"
                aria-describedby="status-flag status-metadata"
                className={element('wrapper', { legacy: !!isLegacy })}
                noShrink
                hAlign="center"
                gap="big"
                orientation="horizontal"
                vAlign="space-between"
            >
                <CloseButton contentId={contentId} hasChanges={hasChanges} />
                {isLoading ? (
                    <FlexBox gap="tiny" orientation="vertical" className={element('informations-wrapper')}>
                        <FlexBox gap="medium" orientation="horizontal" hAlign="center">
                            <SkeletonTypography
                                className={element('title', 'is-loading')}
                                typography="subtitle1"
                                width={100}
                            />
                            <SkeletonRectangle width="l" height="xs" variant="pill" />
                            <LanguageSelectButton
                                onChange={contentEditorActions.onInputLanguageChange}
                                size="s"
                                defaultLanguage={inputLanguage}
                                isVisible
                                inputLanguage={inputLanguage}
                                className={element('language-switcher')}
                                languages={cleanedContributionLanguages}
                                scope={CONTENT_EDITOR_TOPBAR_SCOPE}
                            />
                        </FlexBox>
                        <SkeletonTypography typography="body1" width="100%" />
                    </FlexBox>
                ) : (
                    <FlexBox
                        gap="tiny"
                        orientation="vertical"
                        marginAuto="right"
                        className={element('informations-wrapper')}
                    >
                        <FlexBox gap="medium" orientation="horizontal" hAlign="center">
                            {title ? (
                                <Heading
                                    id="toolbar-title"
                                    className={element('title')}
                                    truncate={{ lines: 2 }}
                                    typography="subtitle1"
                                    {...get({ element: 'content-title' })}
                                >
                                    {title}
                                </Heading>
                            ) : (
                                <Heading
                                    id="toolbar-title"
                                    truncate={{ lines: 2 }}
                                    typography="subtitle1"
                                    color="dark"
                                    colorVariant="L3"
                                    {...get({ element: 'content-title' })}
                                >
                                    {translateKey(GLOBAL.UNTITLED)}
                                </Heading>
                            )}
                            <ContentStatusFlag
                                id="status-flag"
                                {...get({ element: 'status' })}
                                aria-live="polite"
                                status={publicationStatus}
                            />
                            {cleanedContributionLanguages.length > 1 && !isLanguagePopoverSkipped && (
                                <ContentLanguagePopover
                                    isOpen={isLanguagePopoverOpen}
                                    onClose={(skipPopover = false) => {
                                        setIsLanguagePopoverOpen(false);

                                        if (skipPopover) {
                                            cache.store(
                                                LOCAL_STORAGE_CONTENT_LANGUAGE_POPOVER,
                                                skipPopover,
                                                CACHE_TYPE.STORAGE,
                                            );
                                        }
                                    }}
                                    inputLanguage={inputLanguage}
                                    anchorRef={languageSelectRef}
                                />
                            )}
                            <LanguageSelectButton
                                buttonProps={{ buttonRef: languageSelectRef }}
                                onChange={onContributionLanguageChange}
                                size="s"
                                defaultLanguage={inputLanguage}
                                isVisible
                                inputLanguage={inputLanguage}
                                className={element('language-switcher')}
                                languages={cleanedContributionLanguages}
                                scope={CONTENT_EDITOR_TOPBAR_SCOPE}
                            />
                        </FlexBox>

                        <ContentStatusMetadata
                            isHomepage={isHomepage}
                            updatedBy={createdByDetails}
                            refusedReason={refusedReason}
                            updatedAt={updatedAt}
                            hasPendingDraft={hasPendingDraft}
                            expirationDate={expiredAt}
                            scheduledDate={scheduledAt}
                            publicationDate={publishedAt}
                            publicationStatus={publicationStatus}
                            isAwaitingValidation={isAwaitingValidation}
                            isRevisionRequested={isRevisionRequested}
                        />
                    </FlexBox>
                )}
                {!isVersionningPanelOpen && (
                    <FlexBox gap="regular" noShrink marginAuto="left" orientation="horizontal">
                        {publicationStatus !== 'archived' &&
                            (isUnderDesktop ? (
                                <IconButton
                                    label={translateKey(GLOBAL.SETTINGS)}
                                    emphasis="low"
                                    icon={mdiTune}
                                    isDisabled={isLoading}
                                    ref={settingsTriggerRef}
                                    onClick={contentEditorActions.openContentSettings}
                                    {...get({ element: 'publication-options', action: 'open' })}
                                />
                            ) : (
                                <Button
                                    emphasis="low"
                                    leftIcon={mdiTune}
                                    ref={settingsTriggerRef}
                                    isDisabled={isLoading}
                                    onClick={contentEditorActions.openContentSettings}
                                    {...get({ element: 'publication-options', action: 'open' })}
                                >
                                    {translateKey(GLOBAL.SETTINGS)}
                                </Button>
                            ))}

                        {canSave &&
                            (isUnderDesktop ? (
                                <IconButton
                                    label={translateKey(GLOBAL.SAVE)}
                                    emphasis="medium"
                                    icon={mdiContentSave}
                                    onClick={() => contentEditorActions.saveContent()}
                                    isDisabled={isLoading}
                                    {...get({ element: 'publication-workflow', action: 'save' })}
                                />
                            ) : (
                                <Button
                                    emphasis="medium"
                                    onClick={() => contentEditorActions.saveContent()}
                                    isDisabled={isLoading}
                                    {...get({ element: 'publication-workflow', action: 'save' })}
                                >
                                    {translateKey(GLOBAL.SAVE)}
                                </Button>
                            ))}
                        {canUnarchive &&
                            (isUnderDesktop ? (
                                <IconButton
                                    label={translateKey(GLOBAL.UNARCHIVE)}
                                    emphasis="medium"
                                    icon={mdiArchiveArrowUpOutline}
                                    onClick={onUnarchive}
                                    isDisabled={isLoading}
                                    {...get({ element: 'publication-workflow', action: 'unarchive' })}
                                />
                            ) : (
                                <Button
                                    emphasis="medium"
                                    onClick={onUnarchive}
                                    isDisabled={isLoading}
                                    {...get({ element: 'publication-workflow', action: 'unarchive' })}
                                >
                                    {translateKey(GLOBAL.UNARCHIVE)}
                                </Button>
                            ))}
                        {publicationStatus !== 'archived' && (
                            <ButtonGroup>
                                {isUnderDesktop ? (
                                    <IconButton
                                        label={quickAction.label}
                                        onClick={quickAction.onClick}
                                        emphasis="high"
                                        icon={quickAction.shortIcon}
                                        isDisabled={isLoading}
                                        {...quickAction.props}
                                    />
                                ) : (
                                    <Button
                                        isDisabled={isLoading}
                                        onClick={quickAction.onClick}
                                        emphasis="high"
                                        {...quickAction.props}
                                    >
                                        {quickAction.label}
                                    </Button>
                                )}
                                <SecondaryActionsMenu
                                    isHomepage={isHomepage}
                                    isDisabled={isLoading}
                                    onArchive={onArchive}
                                    publicationStatus={publicationStatus}
                                    canArchive={canArchive}
                                    isAwaitingValidation={isAwaitingValidation}
                                    canPublish={canPublish}
                                    requestRevision={onRequestRevision}
                                    unpublishContent={onUnpublish}
                                    scheduleContent={openScheduleDialog({ showSummary: true })}
                                    editContentScheduling={openScheduleDialog({ showSummary: false })}
                                    publishContentNow={publishContentNow}
                                    unscheduleContent={unscheduleContent}
                                />
                            </ButtonGroup>
                        )}
                    </FlexBox>
                )}
            </FlexBox>
        </>
    );
};
